/**
 * useBookingAnalytics - USALI-compliant booking analytics hook
 * ✅ RPC tunnel: get_analytics_bookings (bypasses 1000-row limit)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useToast } from '@/hooks/use-toast';

interface BookingAnalyticsData {
  analytics_date: string;
  total_bookings: number;
  confirmed_bookings: number;
  cancelled_bookings: number;
  pending_bookings: number;
  no_show_bookings: number;
  avg_booking_value: number;
  total_booking_value: number;
}

interface BookingAnalyticsFilters {
  dateFrom: string;
  dateTo: string;
  roomTypes?: string[];
  channels?: string[];
}

export const useBookingAnalytics = (filters: BookingAnalyticsFilters) => {
  const { currentHotel } = useOptimizedHotel();
  const [data, setData] = useState<BookingAnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchData = async () => {
    if (!currentHotel?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🚀 Fetching booking analytics via RPC for hotel:', currentHotel.id);
      const startTime = performance.now();

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_analytics_bookings' as any,
        {
          p_hotel_id: currentHotel.id,
          p_date_from: filters.dateFrom,
          p_date_to: filters.dateTo,
        }
      );

      if (rpcError) throw rpcError;

      const duration = performance.now() - startTime;
      const rows = (rpcData as any[]) || [];
      console.log(`✅ Booking analytics RPC loaded in ${duration.toFixed(1)}ms:`, rows.length, 'days');

      const aggregated: BookingAnalyticsData[] = rows.map((r: any) => ({
        analytics_date: r.date,
        total_bookings: r.total,
        confirmed_bookings: r.confirmed,
        cancelled_bookings: r.cancelled,
        pending_bookings: r.pending,
        no_show_bookings: r.no_show,
        total_booking_value: Number(r.total_value) || 0,
        avg_booking_value: Number(r.avg_value) || 0,
      }));

      setData(aggregated);
    } catch (err: any) {
      setError(err.message);
      console.error('❌ Booking Analytics Error:', err);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить аналитику бронирований",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentHotel?.id, filters.dateFrom, filters.dateTo]);

  // ✅ USALI-compliant summary calculations
  const allBookings = data;
  const totalBookings = allBookings.reduce((sum, item) => sum + item.total_bookings, 0);
  const confirmedBookings = allBookings.reduce((sum, item) => sum + item.confirmed_bookings, 0);
  const cancelledBookings = allBookings.reduce((sum, item) => sum + item.cancelled_bookings, 0);
  const noShowBookings = allBookings.reduce((sum, item) => sum + item.no_show_bookings, 0);
  const pendingBookings = allBookings.reduce((sum, item) => sum + item.pending_bookings, 0);
  const totalRevenue = allBookings.reduce((sum, item) => sum + item.total_booking_value, 0);

  const activeBookings = totalBookings - cancelledBookings;

  const summary = {
    totalBookings,
    confirmedBookings,
    cancelledBookings,
    noShowBookings,
    activeBookings,
    directBookings: 0,
    channelBookings: 0,
    avgBookingValue: totalBookings > 0 ? totalRevenue / totalBookings : 0,
    totalRevenue,
    avgLeadTime: 0,
    conversionRate: totalBookings > 0 ? (confirmedBookings / totalBookings) * 100 : 0,
    cancellationRate: totalBookings > 0 ? (cancelledBookings / totalBookings) * 100 : 0,
    noShowRate: totalBookings > 0 ? (noShowBookings / totalBookings) * 100 : 0,
    directBookingRatio: 0,
  };

  return { data, loading, error, summary, refetch: fetchData };
};
